import React, { useState, useEffect } from 'react';
import { formatPeso } from '../utils/formatPrice';
import { stockSummary } from '../utils/stockAvailability';

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect fill="%23e5e7eb" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="14"%3ENo image%3C/text%3E%3C/svg%3E';

const toneClass = {
  ok: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  low: 'bg-amber-50 text-amber-900 border-amber-200',
  out: 'bg-red-50 text-red-800 border-red-200',
  neutral: 'bg-slate-50 text-slate-800 border-slate-200'
};

export default function ProductDetailModal({
  product,
  backendUrl = '',
  isLoading,
  error,
  onAddToCart,
  onClose,
  storeName
}) {
  const isOpen = !!product || isLoading || !!error;
  const imageSrc = product?.image?.startsWith('http')
    ? product.image
    : product?.image
      ? `${(backendUrl || '').replace(/\/$/, '')}${product.image}`
      : PLACEHOLDER_IMAGE;
  const [imgError, setImgError] = useState(false);
  const [fullImageOpen, setFullImageOpen] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [product?._id, imageSrc]);

  const handleImageError = () => setImgError(true);
  const displaySrc = imgError ? PLACEHOLDER_IMAGE : imageSrc;
  const stock = product ? stockSummary(product, { storeName }) : null;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
        <div className="bg-white p-6 rounded shadow w-full max-w-lg max-h-[90vh] overflow-auto">
          {isLoading ? <p>Loading product details...</p> : null}
          {error ? <p className="text-red-600">{error}</p> : null}
          {product ? (
            <div>
              <div
                className="mb-4 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center min-h-[200px] max-h-[280px] cursor-zoom-in"
                onClick={() => !imgError && displaySrc !== PLACEHOLDER_IMAGE && setFullImageOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && !imgError && displaySrc !== PLACEHOLDER_IMAGE && setFullImageOpen(true)}
                aria-label="View full image"
              >
                <img
                  src={displaySrc}
                  alt={product.name || 'Product'}
                  className="w-full h-full object-contain pointer-events-none"
                  onError={handleImageError}
                />
              </div>
              {!imgError && displaySrc !== PLACEHOLDER_IMAGE && (
                <p className="text-xs text-gray-500 mb-2">Click image to view full size</p>
              )}
            <h3 className="text-xl font-semibold">{product.name}</h3>
            {product.sku ? <p className="text-sm text-gray-600">{product.sku}</p> : null}
            <p className="text-sm text-gray-600">{product.category}</p>
            <p className="mt-2 text-xl font-bold text-slate-900">{formatPeso(product.price)}</p>
            <p className="mt-2 text-sm">
              {product.description || 'No description provided.'}
            </p>
            {stock ? (
              <p
                className={`mt-3 rounded-lg border px-3 py-2 text-sm font-semibold ${toneClass[stock.tone] || toneClass.neutral}`}
              >
                {stock.text}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          {product ? (
            <button
              className="bg-indigo-600 text-white py-1 px-3 rounded text-sm"
              onClick={() => {
                onAddToCart(product);
                onClose();
              }}
            >
              Add to Cart
            </button>
          ) : null}
          <button
            className="border border-gray-300 py-1 px-3 rounded text-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
      </div>

      {fullImageOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Full product image"
        >
          <img
            src={displaySrc}
            alt={product?.name || 'Product full size'}
            className="max-w-full max-h-full object-contain"
          />
          <button
            type="button"
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full w-10 h-10 flex items-center justify-center text-xl"
            onClick={() => setFullImageOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}
